import {
    _allowStateChangesInsideComputed,
    action,
    observable,
    ObservableMap,
    reaction,
} from "mobx";

const AUTOSAVE_DELAY = 2000;

import { QuizObjectCard } from "./quiz-object/quiz-object-card";
import { QuizObjectCardTable } from "./quiz-object/quiz-object-card-table";
import { QuizObjectList } from "./quiz-object/quiz-object-list";
import { QuizObjectTable } from "./quiz-object/quiz-object-table";
import { QuizObjectTableColumn } from "./quiz-object/quiz-object-table-column";

import {
    QuizObjectFact,
    QuizObjectFactsMetadata,
} from "./quiz-object/quiz-object-fact";

import { QuizObjectChunk } from "./quiz-object/quiz-object-chunk";

import { axios } from "../../../utils/axios";

import { SaveStatuses } from "./quiz-object/save-statuses";
import { QuizObjectTracking } from './quiz-object/quiz-object-tracking';

class QuizObject {
    @observable _id: string = "";
    @observable status: Map<string, SaveStatuses> = new Map();
    @observable subject: any = undefined;
    @observable tracking: QuizObjectTracking = new QuizObjectTracking();
    @observable slug: string = "";
    @observable active: boolean = false;
    @observable title: string = "";
    @observable definition: string = "";
    @observable notes: string = "";
    @observable
    factsMetadata: QuizObjectFactsMetadata = new QuizObjectFactsMetadata();
    @observable lists: Array<QuizObjectList> = [];
    @observable card_tables: Array<QuizObjectCardTable> = [];
    @observable tables: Array<QuizObjectTable> = [];
    @observable chunks: Array<QuizObjectChunk> = [];
    @observable types: Array<string> = [];
    @observable user: string = "";
    @observable image_url: string = "";
    @observable quiz_object_hash_key: string = "";
    @observable version: number = 0;
    @observable saveTimer = 5;
    @observable saveInitiated = false;
    @observable persisted = true;
    @observable new: boolean = false;
    @observable savingTitle: boolean = false;
    @observable savingDefinition: boolean = false;
    rootStore: any;

    constructor(slug: string, quiz_object: any, tracking?: any) {
        this._id = quiz_object._id;

        this.tracking = new QuizObjectTracking();

        if (tracking) {
            this.applyTracking(tracking);
        }

        this.definition = this.apply_new(
            this.definition,
            quiz_object.definition
        );
        if (quiz_object.persisted) {
            this.persisted = quiz_object.persisted;
        }
        this.notes = this.apply_new(this.notes, quiz_object.notes);
        this.chunks = this.applyChunks(quiz_object.chunks);
        this.slug = quiz_object.slug;
        this.user = quiz_object.user;
        if (quiz_object.type) {
            this.types = this.str_or_arr_to_arr(quiz_object.type);
        } else {
            this.types = this.str_or_arr_to_arr(quiz_object.types);
        }
        if (quiz_object.facts) {
            this.facts = quiz_object.facts.map((fact: any, i: number) => {
                fact.order = i;
                return new QuizObjectFact(fact);
            });
        }
        if (quiz_object.new) this.new = quiz_object.new;
        this.quiz_object_hash_key = `${quiz_object.user}/${quiz_object.slug}`;
        this.subject = this.applySubject(quiz_object.subject);
        this.title = quiz_object.title;
        this.image_url = quiz_object.image_url;
        this.active = quiz_object.active;

        if (quiz_object.lists) {
            this.lists = quiz_object.lists.map(
                (list: any) =>
                    new QuizObjectList(quiz_object._id, list._id, list)
            );
        }
        if (quiz_object.tables) {
            this.tables = quiz_object.tables.map(
                (table: any) => new QuizObjectTable(quiz_object._id, table)
            );
        }

        const model = this;
        if (quiz_object.card_tables) {
            this.card_tables = quiz_object.card_tables.map(
                (card_table: any) => {
                    return new QuizObjectCardTable(
                        model.subject,
                        model._id,
                        model.status,
                        card_table
                    );
                }
            );
        }

        reaction(
            () => {
                return this.title;
            },
            () => {
                this.patchTitle();
            },
            { delay: AUTOSAVE_DELAY }
        );

        reaction(
            () => {
                return this.definition;
            },
            () => {
                console.log("Definition Changed");
                this.patchDefinition();
            },
            { delay: AUTOSAVE_DELAY }
        );

        reaction(
            () => {
                return this.factsMetadata.toAdd.length;
            },
            () => {
                console.log("Added Facts Changed");
                this.factsMetadata.addQueued(this);
            },
            { delay: AUTOSAVE_DELAY }
        );

        reaction(
            () => {
                return this.factsMetadata.toChange.length;
            },
            () => {
                console.log("Changed Facts Changed");
                this.factsMetadata.changeQueued(this);
            },
            { delay: AUTOSAVE_DELAY }
        );

        reaction(
            () => {
                return this.factsMetadata.toDelete.length;
            },
            () => {
                console.log("Deleted Facts Changed");
                this.factsMetadata.deleteQueued(this);
            },
            { delay: AUTOSAVE_DELAY }
        );

        reaction(
            () => {
                return this.factsMetadata.toRestore.length;
            },
            () => {
                console.log("Deleted Facts Changed");
                this.factsMetadata.restoreQueued(this);
            },
            { delay: AUTOSAVE_DELAY }
        );
    }

    assignTables(tables: Array<any>) { }

    assignLists(lists: Array<any>) { }

    get editable() {
        return true;
    }

    get facts() {
        return this.factsMetadata.facts;
    }

    set facts(facts) {
        this.factsMetadata.facts = facts;
    }

    get saveStatus(): SaveStatuses {
        if (this.pieceStatusesMatch(SaveStatuses.SAVING)) {
            return SaveStatuses.SAVING;
        }

        if (this.pieceStatusesMatch(SaveStatuses.SAVED)) {
            return SaveStatuses.SAVED;
        }

        if (this.pieceStatusesMatch(SaveStatuses.CHANGED)) {
            return SaveStatuses.CHANGED;
        }

        return SaveStatuses.UNCHANGED;
    }

    getFact(factName: string) {
        let matchingFacts = this.facts.filter(fact => {
            return fact.name == factName;
        });

        if (matchingFacts.length > 0) {
            return matchingFacts[0];
        }
        return "";
    }

    getFactById(factID: string): QuizObjectFact | undefined {
        let matchingFacts = this.facts.filter(fact => {
            return fact._id == factID;
        });

        if (matchingFacts.length > 0) {
            return matchingFacts[0];
        }
        return undefined;
    }

    addList() {
        this.lists.push(new QuizObjectList(this._id));
    }

    addTable() {
        var table: QuizObjectTable = new QuizObjectTable(this._id, {
            slug: "",
            name: "",
            type: "",
            description: ""
        });

        this.tables.push(table);
    }

    addChunk() {
        this.chunks.push(
            new QuizObjectChunk(this._id, this.status, {
                content: "",
                isNew: true
            })
        );
    }

    addFact(factName?: string, factValue?: string) {
        if (this.hasFact(factName)) {
            throw "Can't add existing fact";
        }

        let name = "";
        let value = "";

        if (factName) name = factName;
        if (factValue) value = factValue;

        var fact: QuizObjectFact = new QuizObjectFact({
            slug: "",
            name: name,
            valueType: "",
            value: value,
            description: "",
            order: this.facts.length + 1
        });

        this.facts.push(fact);
    }

    str_or_arr_to_arr(val: string | Array<string>): Array<string> {
        if (typeof val === "string") {
            return [val];
        }
        return val;
    }

    removeFact(fact: any) {
        var index = this.facts.indexOf(fact);
        this.facts[index].deleted = true;
        this.factsMetadata.toDelete.push(fact._id);
    }

    restoreFact(fact: any) {
        var index = this.facts.indexOf(fact);
        this.facts[index].deleted = false;
        this.factsMetadata.toRestore.push(fact._id);
    }

    get_num_values(values: any): number {
        if (values instanceof ObservableMap) {
            return values.size;
        }
        return Object.keys(values).length;
    }

    apply_new(old_value: string, new_value: string) {
        if (new_value) {
            return new_value;
        }
        return old_value;
    }

    public applyTracking(tracking: any) {
        tracking.forEach((tracker: any) => {
            if (tracker.dataType == "title") {
                this.tracking.title = tracker.score;
            }
            if (tracker.dataType == "definition") {
                this.tracking.definition = tracker.score;
            }
            if (tracker.dataType == "quiz_object") {
                this.tracking.quiz_object = tracker.score;
            }
        });
    }

    get saving(): boolean {
        return (
            this.status.get("title") == SaveStatuses.SAVING ||
            this.status.get("definition") == SaveStatuses.SAVING
        );
    }

    get saveText(): string {
        if (
            this.status.get("title") == SaveStatuses.CHANGED ||
            this.status.get("definition") == SaveStatuses.CHANGED
        ) {
            return "Saved";
        }

        if (
            this.status.get("title") == SaveStatuses.SAVED ||
            this.status.get("definition") == SaveStatuses.SAVED
        ) {
            return "Saved";
        }

        if (
            this.status.get("title") == SaveStatuses.DELETED ||
            this.status.get("definition") == SaveStatuses.DELETED
        ) {
            return "Deleted";
        }

        if (
            this.status.get("title") == SaveStatuses.DELETING ||
            this.status.get("definition") == SaveStatuses.DELETING
        ) {
            return "Deleting";
        }

        return "Save";
    }

    get link() {
        return `/modquiz/subjects/${this.subject.user}/${this.subject.slug}/o/${this._id}`;
    }

    setTitle(title: string) {
        this.status.set("title", SaveStatuses.CHANGED);
        this.title = title;
    }

    setDefinition(definition: string) {
        this.status.set("definition", SaveStatuses.CHANGED);
        this.definition = definition;
    }

    setFactByIDOrOrder(
        factID: string,
        factOrder: number,
        toSet: string,
        value: string
    ) {
        this.status.set("fact", SaveStatuses.CHANGED);

        let fact_i = undefined;

        this.facts.forEach((fact, i) => {
            if (factID && factID == fact._id) {
                fact_i = i;
                /* TODO: IF it does not exist in there */
                console.log("Pushing onto changed facts");
                if (fact.name && fact.value) {
                    this.factsMetadata.pushToChange(factID);
                }
            } else if (factOrder == fact.order) {
                fact_i = i;
                /* TODO: IF it does not exist in there */
                console.log("Pushing onto added facts");
                if (fact.name && fact.value) {
                    this.factsMetadata.pushToAdd(fact_i);
                }
            }
        });

        if (fact_i !== undefined) {
            (this.facts[fact_i] as any)[toSet] = value;
            return;
        }

        throw "Error: No Fact Matched";
    }

    setField(field: string, value: string) {
        if (field == "definition") {
            this.setDefinition(value);
        }

        if (field == "title") {
            this.setTitle(value);
        }
    }

    hasFact(factName: string | undefined) {
        if (!factName) return false;
        let matchingFacts = this.facts.filter(fact => {
            return fact.name == factName;
        });

        return matchingFacts.length > 0;
    }

    setFact(factName: string, factValue: string) {
        let fact_i = undefined;

        this.facts.forEach((fact, i) => {
            if (fact.name == factName) {
                fact_i = i;
            }
        });

        if (fact_i !== undefined) {
            return (this.facts[fact_i].value = factValue);
        }

        throw "Error: No Fact Matched";
    }

    upsertFact(factName: string, factValue: string) {
        if (this.hasFact(factName)) {
            return this.setFact(factName, factValue);
        }
        this.addFact(factName, factValue);
    }

    applySubject(subject: any) {
        if (!subject) return;
        return {
            slug: subject.slug,
            user: subject.user,
            hash_key: subject.hash_key
        };
    }

    async patchTitle() {
        this.status.set("title", SaveStatuses.SAVING);
        try {
            await axios().patch(`/quiz_objects/${this._id}`, {
                title: this.title
            });
            this.status.set("title", SaveStatuses.SAVED);
        } catch (err) {
            this.status.set("title", SaveStatuses.ERROR);
        }
    }

    async patchDefinition() {
        this.status.set("definition", SaveStatuses.SAVING);
        try {
            await axios().patch(`/quiz_objects/${this._id}`, {
                definition: this.definition
            });
            this.status.set("definition", SaveStatuses.SAVED);
        } catch (err) {
            this.status.set("definition", SaveStatuses.ERROR);
        }
    }

    applyChunks(newChunks: Array<any>) {
        if (newChunks && newChunks.length > 0) {
            return newChunks.map(
                newChunk =>
                    new QuizObjectChunk(this._id, this.status, newChunk)
            );
        }
        return [new QuizObjectChunk(this._id, this.status, { content: "", isNew: true })];
    }


    private pieceStatusesMatch(status: SaveStatuses) {
        return this.status.get("title") == status ||
            this.status.get("definition") == status ||
            this.factsMetadata.status == status ||
            this.status.get("chunk") == status
    }

}

export {
    QuizObject,
    QuizObjectFact,
    QuizObjectList,
    QuizObjectTable,
    QuizObjectTableColumn,
    QuizObjectCardTable,
    QuizObjectCard,
    QuizObjectChunk
};
